export const queryKeys = {
    me: () => ["me"],
    health: () => ["health"],
    club: (clubId) => ["club", clubId],
    books: (clubId) => ["books", clubId],
    members: (clubId) => ["members", clubId],
    discussion: (clubId, bookId) => ["discussion", clubId, bookId],
    notifications: () => ["notifications"],
    notificationCount: () => ["notif-count"],
    notificationPrefs: () => ["notif-prefs"],
    myEmails: () => ["my-emails"],
    /** a person's card; "me" for your own */
    profile: (userId) => ["profile", userId],
    avatarChoices: () => ["avatar-choices"],
    myQuotes: () => ["my-quotes"],
    meeting: (clubId, bookId) => ["meeting", clubId, bookId],
    meetingDetail: (clubId) => ["meeting-detail", clubId],
    notes: (clubId, bookId) => ["notes", clubId, bookId],
    invites: (clubId) => ["invites", clubId],
    joinRequests: (clubId) => ["join-requests", clubId],
    blocks: (clubId) => ["blocks", clubId],
    clubMute: (clubId) => ["club-mute", clubId],
};
export function createHooks(api, rq) {
    const q = (queryKey, queryFn, defaults, o) => rq.useQuery({ queryKey, queryFn, ...defaults, ...o });
    /** A mutation that refreshes the given queries once it lands (before any onSuccess of the caller's). */
    function useWrite(fn, refresh, o) {
        const qc = rq.useQueryClient();
        return rq.useMutation({
            ...o, mutationFn: fn,
            onSuccess: async (data, v, ...rest) => {
                await Promise.all(refresh(v).map((queryKey) => qc.invalidateQueries({ queryKey })));
                await o?.onSuccess?.(data, v, ...rest);
            },
        });
    }
    return {
        useMe: (o) => q(queryKeys.me(), api.me, {}, o),
        useHealth: (o) => q(queryKeys.health(), api.health, { staleTime: 5 * 60_000 }, o),
        useClub: (clubId, o) => q(queryKeys.club(clubId), () => api.club(clubId), {}, o),
        useClubBooks: (clubId, o) => q(queryKeys.books(clubId), () => api.clubBooks(clubId), {}, o),
        useMembers: (clubId, o) => q(queryKeys.members(clubId), () => api.members(clubId), {}, o),
        useDiscussion: (clubId, bookId, o) => q(queryKeys.discussion(clubId, bookId), () => api.discussion(clubId, bookId), {}, o),
        useNotificationCount: (o) => q(queryKeys.notificationCount(), api.notificationCount, { refetchInterval: 60_000, refetchOnWindowFocus: true, staleTime: 15_000 }, o),
        useNotifications: () => rq.useInfiniteQuery({
            queryKey: queryKeys.notifications(), queryFn: ({ pageParam }) => api.notifications(pageParam),
            initialPageParam: null, getNextPageParam: (p) => p.next,
        }),
        // Your account: notification settings, email addresses, library cards and pictures.
        useNotificationPrefs: (o) => q(queryKeys.notificationPrefs(), api.notificationPrefs, {}, o),
        useMyEmails: (o) => q(queryKeys.myEmails(), api.myEmails, {}, o),
        useProfile: (userId, o) => q(queryKeys.profile(userId), () => api.profile(userId), { retry: false }, o),
        useAvatarChoices: (o) => q(queryKeys.avatarChoices(), api.avatarChoices, { staleTime: 5 * 60_000 }, o),
        useMyQuotes: (o) => q(queryKeys.myQuotes(), api.myQuotes, {}, o),
        /** Saves notification settings and puts the answer straight into the cache. */
        useUpdateNotificationPrefs: (o) => {
            const qc = rq.useQueryClient();
            return rq.useMutation({ ...o, mutationFn: api.updateNotificationPrefs,
                onSuccess: async (p, ...rest) => { qc.setQueryData(queryKeys.notificationPrefs(), p); await o?.onSuccess?.(p, ...rest); } });
        },
        /** Name, bio, genres, pinned quote, card visibility: every card and "me" refresh. */
        useUpdateProfile: (o) => useWrite((v) => api.updateProfile(v), () => [["profile"], queryKeys.me()], o),
        /** Your progress on a book: refreshes the club's books and that book's discussion (what you can read changes). */
        useUpdateProgress: (clubId, bookId, o) => useWrite((v) => api.updateProgress(clubId, bookId, v), () => [queryKeys.books(clubId), queryKeys.discussion(clubId, bookId)], o),
        /** A message, reply, quote or question in Talk. */
        useAddComment: (clubId, bookId, o) => useWrite((v) => api.addComment(clubId, bookId, v), () => [queryKeys.discussion(clubId, bookId)], o),
        useEditComment: (clubId, bookId, o) => useWrite(({ id, ...v }) => api.editComment(clubId, id, v), () => [queryKeys.discussion(clubId, bookId)], o),
        useDeleteComment: (clubId, bookId, o) => useWrite((id) => api.deleteComment(clubId, id), () => [queryKeys.discussion(clubId, bookId)], o),
        useMarkRead: (o) => useWrite((v) => api.markRead(v), () => [queryKeys.notifications(), queryKeys.notificationCount()], o),
        // The Meeting and Club tabs, the notebook (keys as the web's pages use them).
        useMeeting: (clubId, bookId, o) => q(queryKeys.meeting(clubId, bookId), () => api.meeting(clubId, bookId), {}, o),
        useMeetingDetail: (clubId, o) => q(queryKeys.meetingDetail(clubId), () => api.meetingDetail(clubId), {}, o),
        useNotes: (clubId, bookId, o) => q(queryKeys.notes(clubId, bookId), () => api.notes(clubId, bookId), {}, o),
        useInvites: (clubId, o) => q(queryKeys.invites(clubId), () => api.invites(clubId), {}, o),
        useJoinRequests: (clubId, o) => q(queryKeys.joinRequests(clubId), () => api.joinRequests(clubId), {}, o),
        useBlocks: (clubId, o) => q(queryKeys.blocks(clubId), () => api.blocks(clubId), {}, o),
        useClubMute: (clubId, o) => q(queryKeys.clubMute(clubId), () => api.clubMute(clubId), {}, o),
    };
}
//# sourceMappingURL=hooks.js.map